import { createContext, useContext, useEffect, useState } from 'react';
import { BaseOneServiceI18n, ContentfulBackend } from '@soluto-private/mx-i18n';
import { InitOptions } from 'i18next';
import { InitI18nOptions } from '../types';

export const I18nContext = createContext<BaseOneServiceI18n | undefined>(undefined);
export const useI18n = () => useContext(I18nContext);

const getDefaultFromNS = (ns: InitOptions['ns']) =>
  Array.isArray(ns) ? (ns as string[])[0] : (ns as string | undefined);

// eslint-disable-next-line @typescript-eslint/ban-types
export const initI18n = async <T extends object>({
  ns,
  defaultNS,
  fallbackNS,
  load,
  plugins,
  backendModule,
  backendOptions,
}: InitI18nOptions<T>) => {
  const instance = new BaseOneServiceI18n();

  await instance.init({
    plugins: [backendModule ?? ContentfulBackend, ...plugins],
    init: {
      ns,
      defaultNS: defaultNS ?? getDefaultFromNS(ns),
      fallbackNS: fallbackNS ?? getDefaultFromNS(ns),
      load: load ?? 'all',
      fallbackLng: false, // use contentful default
      interpolation: {
        escapeValue: false,
        defaultVariables: {
          whitespace: ' ',
        },
      },
      backend: backendOptions,
    },
  });

  return instance;
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const createUseInitI18n = <T extends object>(
  getOptions: () => InitI18nOptions<T> | Promise<InitI18nOptions<T>>
) => (): BaseOneServiceI18n | undefined => {
  const [i18n, setI18n] = useState<BaseOneServiceI18n>();

  useEffect(() => {
    const init = async () => {
      const options = await getOptions();
      const i18nInit = await initI18n(options);
      setI18n(i18nInit);
    };

    void init();
  }, []);

  return i18n;
};
